import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { DisplayNameModel } from '@/models/core'
import type { Writable } from '@/types.ts'
import logging from '@/utils/logging'

export type SelectionList = number[] | Writable<DisplayNameModel[]>

export const useItemSelectionStore = defineStore('itemSelection', () => {
  // State
  const itemSelections = ref<Record<string, SelectionList>>({})

  // Actions
  const setItemSelection = (uniqueViewName: string, payload: SelectionList): void => {
    if (payload.length === 0 || typeof payload[0] === 'number') {
      for (const idx in payload) {
        const item = payload[idx]
        if (typeof item !== 'number' || isNaN(item)) {
          logging.error(`Ungültiges Element bei Index ${idx} im number Array: ${item} für uniqueViewName ${uniqueViewName} – Payload: ${JSON.stringify(payload)}`)
          return
        }
      }
    } else {
      for (const idx in payload) {
        const item = payload[idx]
        // Kontrolle auf display_name wurden hier entfernt, da es Listen ohne display_name unnutzbar machen würde
        // organisation#11875
        if (!item || typeof item !== 'object' || !('id' in item)) {
          logging.error(`Ungültiges Element bei Index ${idx} im DisplayNameModel Array: ${item} für uniqueViewName ${uniqueViewName} – Payload: ${JSON.stringify(payload)}`)
          return
        }
        if (!('display_name' in item)) {
          (payload[idx] as Writable<DisplayNameModel>).display_name = `${(item as DisplayNameModel).id}` // Fallback auf ID als display_name
        }
      }
    }
    itemSelections.value[uniqueViewName] = payload
  }

  const clearItemSelection = (uniqueViewName: string): void => {
    delete itemSelections.value[uniqueViewName]
  }

  // Alle Selektionen leeren (z.B. bei Route-Wechsel)
  const clearAllItemSelections = (): void => {
    itemSelections.value = {}
  }

  // Getters (als computed)
  const getSelectedItems = computed(() => {
    return (uniqueViewName: string): SelectionList => {
      return itemSelections.value[uniqueViewName] || []
    }
  })

  const getSelectedIds = computed(() => {
    return (uniqueViewName: string): number[] => {
      const items = itemSelections.value[uniqueViewName] || []
      // Wenn es bereits ein number[] ist, direkt zurückgeben
      if (items.length === 0 || typeof items[0] === 'number') {
        return items as number[]
      }        
      // Wenn es DisplayNameModel[] ist, IDs extrahieren
      const ids: number[] = []
      for (const item of items) {
        ids.push((item as DisplayNameModel).id)
      }
      return ids
    }
  })

  const getSelectionCount = computed(() => {
    return (uniqueViewName: string): number => {
      return itemSelections.value[uniqueViewName]?.length || 0
    }
  })

  return {
    // State
    itemSelections,
    // Actions
    setItemSelection,
    clearItemSelection,
    clearAllItemSelections,
    // Getters
    getSelectedItems,
    getSelectedIds,
    getSelectionCount
  }
})